import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2, Calendar, Clock, CalendarDays, Euro, MapPin } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PageContainer } from "@/components/pro/PageContainer";
import { PageHeader } from "@/components/pro/PageHeader";
import eventHerbsWatermark from "@/assets/event-herbs-watermark.jpg";
import eventMeditationWatermark from "@/assets/event-meditation-watermark.jpg";
import eventAromatherapyWatermark from "@/assets/event-aromatherapy-watermark.jpg";
import eventNatureWatermark from "@/assets/event-nature-watermark.jpg";

interface Event {
  id: string;
  title: string;
  short_description: string;
  full_description: string;
  duration: string;
  format: string;
  price: number;
  objectives: string[];
  program: string;
  practical_info: string;
  image_url: string | null;
  is_active: boolean;
  booking_enabled: boolean;
  display_order: number;
  event_date?: string;
  max_spots?: number;
  available_spots?: number;
}

export default function EventsManagement() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: "",
    short_description: "",
    full_description: "",
    duration: "",
    format: "Présentiel",
    price: 0,
    objectives: "",
    program: "",
    practical_info: "",
    image_url: "",
    is_active: false,
    booking_enabled: false,
    display_order: 0,
    event_date: "",
    max_spots: 0,
    available_spots: 0,
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const createOrUpdateEventAppointment = async (eventId: string, eventData: any, userId: string) => {
    try {
      // Check if appointment already exists for this event
      const { data: existingApt } = await supabase
        .from("appointments")
        .select("id")
        .eq("event_id", eventId)
        .maybeSingle();

      const appointmentDateTime = new Date(eventData.event_date);
      const appointmentTime = appointmentDateTime.toLocaleTimeString("fr-FR", {
        hour: "2-digit",
        minute: "2-digit",
      });

      const aptData = {
        service_type: `ÉVÉNEMENT: ${eventData.title}`,
        appointment_date: eventData.event_date,
        appointment_time: appointmentTime,
        duration_minutes: 180, // Default 3h for events
        is_online: eventData.format === "En ligne",
        status: "confirmed",
        event_id: eventId,
        practitioner_id: userId,
        notes: `Places réservées: ${(eventData.max_spots || 0) - (eventData.available_spots || 0)}/${eventData.max_spots || 0} | Places restantes: ${eventData.available_spots || 0}`,
      };

      if (existingApt) {
        // Update existing appointment
        await supabase
          .from("appointments")
          .update(aptData)
          .eq("id", existingApt.id);
      } else {
        // Create new appointment
        await supabase
          .from("appointments")
          .insert(aptData);
      }
    } catch (error) {
      console.error("Error creating/updating event appointment:", error);
    }
  };

  const fetchEvents = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("user_id", user.id)
        .order("display_order", { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les événements",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const objectivesArray = formData.objectives
        .split("\n")
        .filter((obj) => obj.trim() !== "");

      // Convert local datetime to proper timestamp
      // datetime-local returns "YYYY-MM-DDTHH:mm" without timezone
      // We need to treat this as local time and convert to ISO string
      let eventDateUTC = null;
      if (formData.event_date) {
        // Parse the datetime-local value
        const [datePart, timePart] = formData.event_date.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes] = timePart.split(':').map(Number);
        
        // Create date in local timezone with these values
        const localDate = new Date(year, month - 1, day, hours, minutes);
        eventDateUTC = localDate.toISOString();
      }

      const eventData = {
        ...formData,
        objectives: objectivesArray,
        user_id: user.id,
        event_date: eventDateUTC,
        max_spots: formData.max_spots || null,
        available_spots: formData.available_spots || null,
      };

      if (editingEvent) {
        const { error } = await supabase
          .from("events")
          .update(eventData)
          .eq("id", editingEvent.id);

        if (error) throw error;
        
        // Si événement activé et a une date, créer/mettre à jour appointment
        if (eventData.is_active && eventData.event_date) {
          await createOrUpdateEventAppointment(editingEvent.id, eventData, user.id);
        } else if (!eventData.is_active) {
          // Si événement désactivé, supprimer l'appointment de l'agenda
          await supabase
            .from("appointments")
            .delete()
            .eq("event_id", editingEvent.id);
        }
        
        toast({ title: "Événement modifié avec succès" });
      } else {
        const { data: newEvent, error } = await supabase
          .from("events")
          .insert([eventData])
          .select()
          .single();
          
        if (error) throw error;
        
        // If event is active and has a date, create appointment slot
        if (eventData.is_active && eventData.event_date && newEvent) {
          await createOrUpdateEventAppointment(newEvent.id, eventData, user.id);
        }
        
        toast({ title: "Événement créé avec succès" });
      }

      setDialogOpen(false);
      resetForm();
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder l'événement",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    
    // Convert UTC timestamp back to local datetime for the input
    let localDateTimeStr = "";
    if (event.event_date) {
      const utcDate = new Date(event.event_date);
      // Format: YYYY-MM-DDTHH:mm for datetime-local input
      const year = utcDate.getFullYear();
      const month = String(utcDate.getMonth() + 1).padStart(2, '0');
      const day = String(utcDate.getDate()).padStart(2, '0');
      const hours = String(utcDate.getHours()).padStart(2, '0');
      const minutes = String(utcDate.getMinutes()).padStart(2, '0');
      localDateTimeStr = `${year}-${month}-${day}T${hours}:${minutes}`;
    }
    
    setFormData({
      title: event.title,
      short_description: event.short_description,
      full_description: event.full_description,
      duration: event.duration,
      format: event.format,
      price: event.price,
      objectives: event.objectives.join("\n"),
      program: event.program,
      practical_info: event.practical_info,
      image_url: event.image_url || "",
      is_active: event.is_active,
      booking_enabled: event.booking_enabled,
      display_order: event.display_order,
      event_date: localDateTimeStr,
      max_spots: event.max_spots || 0,
      available_spots: event.available_spots || 0,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet événement ?")) return;

    try {
      const { error } = await supabase.from("events").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Événement supprimé avec succès" });
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'événement",
        variant: "destructive",
      });
    }
  };

  const handleResetRegistrations = async (eventId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer toutes les inscriptions à cet événement ?")) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Delete all registrations
      const { error: deleteError } = await supabase
        .from("event_registrations")
        .delete()
        .eq("event_id", eventId);

      if (deleteError) throw deleteError;

      // Delete all appointments linked to this event
      const { error: appointmentsError } = await supabase
        .from("appointments")
        .delete()
        .eq("event_id", eventId);

      if (appointmentsError) throw appointmentsError;

      // Update available spots back to max_spots
      const event = events.find(e => e.id === eventId);
      if (event && event.max_spots) {
        await supabase
          .from("events")
          .update({ available_spots: event.max_spots })
          .eq("id", eventId);

        // Recreate the main event appointment with reset info
        if (event.is_active && event.event_date) {
          await createOrUpdateEventAppointment(eventId, {
            ...event,
            available_spots: event.max_spots,
            max_spots: event.max_spots
          }, user.id);
        }
      }

      toast({ title: "Inscriptions supprimées avec succès" });
      fetchEvents();
    } catch (error) {
      console.error("Error resetting registrations:", error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer les inscriptions",
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (event: Event) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const newActiveState = !event.is_active;
      
      const { error } = await supabase
        .from("events")
        .update({ is_active: newActiveState })
        .eq("id", event.id);

      if (error) throw error;

      if (newActiveState && event.event_date) {
        // ACTIVATION : créer/mettre à jour l'appointment dans l'agenda
        const { data: registrations } = await supabase
          .from("event_registrations")
          .select("id")
          .eq("event_id", event.id);

        const registeredCount = registrations?.length || 0;
        const maxSpots = event.max_spots || 0;
        const availableSpots = maxSpots - registeredCount;

        await createOrUpdateEventAppointment(event.id, {
          ...event,
          available_spots: availableSpots,
          max_spots: maxSpots
        }, user.id);
        
        toast({
          title: "Événement activé et ajouté à l'agenda",
        });
      } else if (!newActiveState) {
        // DÉSACTIVATION : supprimer l'appointment de l'agenda
        const { error: deleteError } = await supabase
          .from("appointments")
          .delete()
          .eq("event_id", event.id);

        if (deleteError) {
          console.error("Error deleting event appointment:", deleteError);
        }
        
        toast({
          title: "Événement désactivé et retiré de l'agenda",
        });
      }
      
      fetchEvents();
    } catch (error) {
      console.error("Error toggling event:", error);
      toast({
        title: "Erreur",
        description: "Impossible de modifier l'événement",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      short_description: "",
      full_description: "",
      duration: "",
      format: "Présentiel",
      price: 0,
      objectives: "",
      program: "",
      practical_info: "",
      image_url: "",
      is_active: false,
      booking_enabled: false,
      display_order: 0,
      event_date: "",
      max_spots: 0,
      available_spots: 0,
    });
    setEditingEvent(null);
  };

  if (loading) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-accent/10 flex items-center justify-center animate-pulse">
            <CalendarDays className="w-6 h-6 text-accent" />
          </div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Événements & Stages"
        subtitle="Gérez vos ateliers et stages à afficher sur votre site vitrine"
        icon={<CalendarDays className="w-6 h-6 sm:w-7 sm:h-7" />}
        iconColor="accent"
        actions={
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-gradient-primary">
                <Plus className="h-4 w-4" />
                Nouvel événement
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-cinzel">
                  {editingEvent ? "Modifier l'événement" : "Créer un nouvel événement"}
                </DialogTitle>
              </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="short_description">Description courte *</Label>
                <Textarea
                  id="short_description"
                  value={formData.short_description}
                  onChange={(e) =>
                    setFormData({ ...formData, short_description: e.target.value })
                  }
                  rows={2}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="full_description">Description complète *</Label>
                <Textarea
                  id="full_description"
                  value={formData.full_description}
                  onChange={(e) =>
                    setFormData({ ...formData, full_description: e.target.value })
                  }
                  rows={4}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="duration">Durée *</Label>
                  <Input
                    id="duration"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    placeholder="Ex: 3h, 1 journée"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="format">Format *</Label>
                  <Select
                    value={formData.format}
                    onValueChange={(value) => setFormData({ ...formData, format: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Présentiel">Présentiel</SelectItem>
                      <SelectItem value="Visio">Visio</SelectItem>
                      <SelectItem value="Présentiel / Visio">Présentiel / Visio</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">Tarif (€) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) =>
                    setFormData({ ...formData, price: parseFloat(e.target.value) })
                  }
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="objectives">Objectifs (un par ligne)</Label>
                <Textarea
                  id="objectives"
                  value={formData.objectives}
                  onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                  rows={4}
                  placeholder="Objectif 1&#10;Objectif 2&#10;Objectif 3"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="program">Programme détaillé *</Label>
                <Textarea
                  id="program"
                  value={formData.program}
                  onChange={(e) => setFormData({ ...formData, program: e.target.value })}
                  rows={5}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="practical_info">Informations pratiques *</Label>
                <Textarea
                  id="practical_info"
                  value={formData.practical_info}
                  onChange={(e) =>
                    setFormData({ ...formData, practical_info: e.target.value })
                  }
                  rows={3}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="image_url">URL de l'image (optionnel)</Label>
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_order">Ordre d'affichage</Label>
                <Input
                  id="display_order"
                  type="number"
                  value={formData.display_order}
                  onChange={(e) =>
                    setFormData({ ...formData, display_order: parseInt(e.target.value) })
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_active: checked })
                    }
                  />
                  <Label htmlFor="is_active">Afficher sur le site vitrine</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="booking_enabled"
                    checked={formData.booking_enabled}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, booking_enabled: checked })
                    }
                  />
                  <Label htmlFor="booking_enabled">Activer les réservations</Label>
                </div>
              </div>

              {formData.booking_enabled && (
                <div className="space-y-4 p-4 bg-secondary/10 rounded-lg border border-secondary/20">
                  <h3 className="font-semibold text-sm text-secondary">Paramètres de réservation</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="event_date">Date de l'événement *</Label>
                    <Input
                      id="event_date"
                      type="datetime-local"
                      value={formData.event_date}
                      onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                      required={formData.booking_enabled}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="max_spots">Nombre de places total *</Label>
                      <Input
                        id="max_spots"
                        type="number"
                        min="0"
                        value={formData.max_spots}
                        onChange={(e) => {
                          const maxSpots = parseInt(e.target.value) || 0;
                          setFormData({ 
                            ...formData, 
                            max_spots: maxSpots,
                            available_spots: editingEvent ? formData.available_spots : maxSpots
                          });
                        }}
                        required={formData.booking_enabled}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="available_spots">Places disponibles *</Label>
                      <Input
                        id="available_spots"
                        type="number"
                        min="0"
                        max={formData.max_spots}
                        value={formData.available_spots}
                        onChange={(e) => setFormData({ ...formData, available_spots: parseInt(e.target.value) || 0 })}
                        required={formData.booking_enabled}
                      />
                      <p className="text-xs text-muted-foreground">
                        Ce nombre se décrémente automatiquement à chaque réservation
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit">
                  {editingEvent ? "Modifier" : "Créer"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.map((event, index) => {
          // Sélectionner une image de fond selon l'index
          const backgroundImages = [
            eventHerbsWatermark,
            eventMeditationWatermark,
            eventAromatherapyWatermark,
            eventNatureWatermark,
          ];
          const backgroundImage = backgroundImages[index % backgroundImages.length];

          return (
            <Card key={event.id} className={`relative overflow-hidden ${!event.is_active ? "opacity-60" : ""}`}>
              {/* Image de fond en filigrane */}
              <div
                className="absolute inset-0 opacity-10 bg-cover bg-center pointer-events-none"
                style={{ backgroundImage: `url(${backgroundImage})` }}
              />

              <CardHeader className="relative z-10">
                <div className="flex justify-between items-start">
                  <CardTitle className="text-xl">{event.title}</CardTitle>
                  <Switch
                    checked={event.is_active}
                    onCheckedChange={() => toggleActive(event)}
                  />
                </div>
                <CardDescription className="line-clamp-2">
                  {event.short_description}
                </CardDescription>
              </CardHeader>
              <CardContent className="relative z-10">
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    {event.duration}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    {event.format}
                  </div>
                  {event.event_date && (
                    <div className="text-sm text-muted-foreground">
                      📅 {new Date(event.event_date).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                      {" à "}
                      {new Date(event.event_date).toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </div>
                  )}
                  {event.booking_enabled && event.max_spots && (
                    <div className="text-sm">
                      <span className="text-muted-foreground">Places: </span>
                      <span className={`font-medium ${(event.available_spots || 0) === 0 ? 'text-destructive' : 'text-primary'}`}>
                        {event.available_spots}/{event.max_spots}
                      </span>
                    </div>
                  )}
                  <div className="text-lg font-semibold text-primary">
                    {event.price}€
                  </div>
                  <div className="text-xs">
                    <span className={`inline-block px-2 py-1 rounded ${event.booking_enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {event.booking_enabled ? '✓ Réservation active' : 'Réservation désactivée'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEdit(event)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Modifier
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(event.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={() => handleResetRegistrations(event.id)}
                  >
                    Reset inscriptions
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {events.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">
            Aucun événement créé. Cliquez sur "Nouvel événement" pour commencer.
          </p>
        </div>
      )}
    </PageContainer>
  );
}
